import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function BMRCalc() {
  const [tab, setTab] = useState(0);
  const [age, setAge] = useState(25);
  const [gender, setGender] = useState('male');
  const [ft, setFt] = useState(5);
  const [inches, setInches] = useState(10);
  const [lb, setLb] = useState(160);
  const [cm, setCm] = useState(178);
  const [kg, setKg] = useState(73);
  // Settings — formula & body fat
  const [formula, setFormula] = useState('mifflin'); // mifflin | harris | katch
  const [bodyFatPct, setBodyFatPct] = useState(20);

  const clear = () => {
    setAge(25); setGender('male'); setFt(5); setInches(10); setLb(160); setCm(178); setKg(73);
    setFormula('mifflin'); setBodyFatPct(20);
  };

  const c = useMemo(() => {
    const heightCm = tab === 0 ? ((+ft || 0) * 12 + (+inches || 0)) * 2.54 : +cm || 0;
    const weightKg = tab === 0 ? (+lb || 0) * 0.453592 : +kg || 0;
    let bmr = 0;
    if (formula === 'mifflin') {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * age + (gender === 'male' ? 5 : -161);
    } else if (formula === 'harris') {
      bmr = gender === 'male'
        ? 88.362 + 13.397 * weightKg + 4.799 * heightCm - 5.677 * age
        : 447.593 + 9.247 * weightKg + 3.098 * heightCm - 4.330 * age;
    } else {
      const lean = weightKg * (1 - (+bodyFatPct || 0) / 100);
      bmr = 370 + 21.6 * lean;
    }
    return {
      heightCm, weightKg, bmr,
      sedentary: bmr * 1.2,
      light: bmr * 1.375,
      moderate: bmr * 1.465,
      active: bmr * 1.55,
      veryActive: bmr * 1.725,
      extraActive: bmr * 1.9,
    };
  }, [tab, age, gender, ft, inches, lb, cm, kg, formula, bodyFatPct]);

  const heightInputs = tab === 0 ? (
    <div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={ft} inches={inches} onFt={setFt} onIn={setInches} testidPrefix="bmr-h" /></div>
  ) : <UnitInput value={cm} onChange={setCm} unit="cm" testid="bmr-h-cm" />;
  const weightInputs = tab === 0
    ? <UnitInput value={lb} onChange={setLb} unit="pounds" testid="bmr-w-lb" />
    : <UnitInput value={kg} onChange={setKg} unit="kg" testid="bmr-w-kg" />;

  return (
    <CalcShell look="cnet" title="BMR Calculator" subtitle="Estimate the calories your body burns at complete rest (basal metabolic rate).">
      <CalcNetPanel
        slug="bmr"
        activeTab={tab}
        onTabChange={setTab}
        onClear={clear}
        settings={
          <>
            <Row label="BMR Formula">
              <select value={formula} onChange={e => setFormula(e.target.value)} data-testid="bmr-formula"
                className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                <option value="mifflin">Mifflin-St Jeor Equation</option>
                <option value="harris">Revised Harris-Benedict Equation</option>
                <option value="katch">Katch-McArdle Formula (needs Body Fat)</option>
              </select>
            </Row>
            {formula === 'katch' && (
              <Row label="Body Fat"><UnitInput value={bodyFatPct} onChange={setBodyFatPct} unit="%" testid="bmr-bf" /></Row>
            )}
          </>
        }
        right={
          <div>
            <div className="text-xl md:text-2xl font-semibold text-slate-900">
              BMR = <span className="text-[#2f9c3f] font-bold">{Math.round(c.bmr).toLocaleString()}</span> Calories/day
            </div>
            <p className="mt-4 mb-2 text-sm text-slate-700">Daily calorie needs based on activity level</p>
            <ResultTable
              headers={['Activity Level', 'Calorie']}
              testid="bmr-activity-table"
              rows={[
                ['Sedentary: little or no exercise', Math.round(c.sedentary).toLocaleString()],
                ['Exercise 1-3 times/week', Math.round(c.light).toLocaleString()],
                ['Exercise 4-5 times/week', Math.round(c.moderate).toLocaleString()],
                ['Daily exercise or intense exercise 3-4 times/week', Math.round(c.active).toLocaleString()],
                ['Intense exercise 6-7 times/week', Math.round(c.veryActive).toLocaleString()],
                ['Very intense exercise daily, or physical job', Math.round(c.extraActive).toLocaleString()],
              ]}
            />
            <div className="mt-3 text-xs text-slate-700 leading-relaxed">
              <p><b>Exercise:</b> 15-30 minutes of elevated heart rate activity.</p>
              <p><b>Intense exercise:</b> 45-120 minutes of elevated heart rate activity.</p>
              <p><b>Very intense exercise:</b> 2+ hours of elevated heart rate activity.</p>
            </div>
          </div>
        }
      >
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="bmr-age" /><span className="text-slate-500 text-xs whitespace-nowrap">ages 15 – 80</span></Row>
        <Row label="Gender"><RadioGroup name="bmr-gender" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        <Row label="Height">{heightInputs}</Row>
        <Row label="Weight">{weightInputs}</Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        formula === 'mifflin' ? `Mifflin-St Jeor: BMR = 10·W + 6.25·H − 5·A + ${gender === 'male' ? '5' : '−161'}` :
        formula === 'harris' ? `Revised Harris-Benedict formula (${gender})` :
        `Katch-McArdle: BMR = 370 + 21.6 × Lean Body Mass`,
        `Weight = ${c.weightKg.toFixed(1)} kg · Height = ${c.heightCm.toFixed(0)} cm · Age = ${age}`,
        `BMR = <b>${Math.round(c.bmr).toLocaleString()}</b> Calories/day`,
      ]} />
    </CalcShell>
  );
}
